import React from "reactn";

import { $ } from "js/vendor";
import { app } from "js/namespaces";
import { renderReactDialog } from "js/react/renderReactRoot";
import { trackActivity } from "js/core/utilities/utilities";
import {
    PersonalRemoveBrandingDialog,
    PersonalSlideLimitUpgradePlanDialog,
    TeamSlideLimitUpgradePlanDialog,
    TeamUpgradePlanDialog,
    PersonalUpgradePlanDialog,
    UpgradePlanDialogType
} from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import { FeatureType } from "common/features";
import { getPricingPageUrl } from "js/core/utilities/pricing";
import ConfirmationScaryDialog from "./ConfirmationScaryDialog";
import MessageDialog from "./MessageDialog";
import ErrorDialog from "./ErrorDialog";
import InputDialog from "js/react/components/Dialogs/InputDialog";
import { getStaticUrl, isRenderer } from "js/config";

export { ShowSnackBar } from "./SnackBar";

if (!app.dialogManager) {
    app.dialogManager = {
        openDialogs: [],
        trackHistory: window.disableHistory === true ? false : true,
        getOpenDialogByType(type) {
            for (let i = 0; i < this.openDialogs.length; i++) {
                if (this.openDialogs[i].type === type) {
                    return this.openDialogs[i];
                }
            }
            return null;
        },
        registerDialog(dialog) {
            if (dialog && dialog.lockSlide && app.currentCanvas) {
                app.currentCanvas.lockSlideForCollaborators(24 * 60 * 60); // 24 hours
                app.currentCanvas.freezeCollaboratorsLock = true;
            }

            this.openDialogs.push(dialog);

            if (app.currentCanvas) {
                app.currentCanvas.stopPlayback();
            }

            if (this.openDialogs.length == 1) {
                if (dialog && dialog.allowUndo == false && app.undoManager) {
                    app.undoManager.enabled = false;
                }
                if (this.trackHistory) {
                    $(window).on("popstate.dialog", event => {
                        if (dialog.close) dialog.close();
                        else if (dialog.props.closeDialog) dialog.props.closeDialog();
                        event.stopPropagation();
                        event.preventDefault();
                    });
                }
            }

            if (this.trackHistory) {
                window.history.pushState("dialog", null, "");
            }
        },
        unregisterDialog(dialog) {
            if (dialog && dialog.lockSlide && app.currentCanvas) {
                app.currentCanvas.freezeCollaboratorsLock = false;
                app.currentCanvas.unlockSlideForCollaborators();
            }

            this.openDialogs.remove(dialog);

            if (this.openDialogs.length == 0) {
                if (app.undoManager) {
                    app.undoManager.enabled = true;
                }
                $(window).off("popstate.dialog");
            }
        }
    };
}

export { default as DialogContent } from "./DialogContent";

export { default as BeautifulDialog } from "./BeautifulDialog";

export const ShowDialog = (dialog, props = {}) => {
    if (isRenderer) {
        console.warn("Renderer should not show dialogs.");
        return;
    }
    return renderReactDialog(dialog, props);
};

export const ShowDialogAsync = (dialog, props = {}) => {
    if (isRenderer) {
        console.warn("Renderer should not show dialogs.");
        return;
    }
    return new Promise(resolve => {
        renderReactDialog(dialog, {
            ...props,
            onClose: result => {
                props.onClose && props.onClose(result);
                resolve(result);
            }
        });
    });
};

export const ShowInputDialog = props => ShowDialogAsync(InputDialog, props);

export const ShowMessageDialog = props => ShowDialogAsync(MessageDialog, props);

export const ShowConfirmationDialog = props => ShowDialogAsync(ConfirmationScaryDialog, props);

export const ShowErrorDialog = props => ShowDialogAsync(ErrorDialog, props);

export const ShowWarningDialog = (props = { title: null, message: null, acceptCallback: null }) => {
    // Mitch: I don't love this but we need to remove any open dropdown menus when a warning
    //  is shown and it's tricky to do that within a callback on a control in a popupmenu
    $(".dropdown_menu").remove();

    return ShowErrorDialog({
        ...props,
        acceptOnClose: true
    });
};

export const ShowOfflineDialog = () => ShowErrorDialog({
    title: "Sorry!",
    message: "Your connection appears to be offline, and this functionality is only available while you are online."
});

export const ShowUnsupportedFileTypesDialog = () => ShowErrorDialog({
    title: "Sorry, we don't support that type of file",
    message: "Please select a JPEG, PNG, GIF, or SVG image file or a video file."
});

export const ShowConvertedToClassicDialog = () => ShowConfirmationDialog({
    title: <>
        <img width={40} src={getStaticUrl("/images/dialogs/convert-to-classic.svg")} />
        <div>You've removed the design guardrails by converting to Classic.</div>
    </>,
    showOptOutCheckbox: true,
    optOutProperty: "showConvertedToClassicDialog",
    message: "You can revert to the Smart Slide in Version History.",
    align: "center"
});

export const ShowUpgradeDialog = ({ type, workspaceId, analytics }) => {
    if (!type) {
        throw new Error("'type' is required");
    }
    if (!workspaceId) {
        throw new Error("'workspaceId' is required");
    }
    if (!analytics) {
        throw new Error("'analytics' is required");
    }

    let view;
    if (workspaceId === "personal") {
        switch (type) {
            case UpgradePlanDialogType.UPGRADE_PLAN:
                view = PersonalUpgradePlanDialog;
                break;
            case UpgradePlanDialogType.EXCEEDED_SLIDE_LIMIT:
                view = PersonalSlideLimitUpgradePlanDialog;
                break;
            case UpgradePlanDialogType.REMOVE_BRANDING:
                view = PersonalRemoveBrandingDialog;
                break;
            case UpgradePlanDialogType.UPGRADE_PLAN_TO_TEAM:
                const userIsBasicInWorkspace = app.user.features.isFeatureEnabled(FeatureType.UPGRADE, workspaceId);
                const currentPlan = userIsBasicInWorkspace ? "basic" : app.user.analyticsPersonalPlan;
                window.open(getPricingPageUrl(app && app.user.hasTakenTrial, app && app.user.has("hasTakenTeamTrial"), currentPlan), "_blank");
                return;
            case UpgradePlanDialogType.TEAM_NOOP:
                // we don't have a dialog for this case currently,
                // Add Slide has it's own CTA
                return;
        }
    } else {
        switch (type) {
            case UpgradePlanDialogType.EXCEEDED_SLIDE_LIMIT:
                view = TeamSlideLimitUpgradePlanDialog;
                break;
            case UpgradePlanDialogType.UPGRADE_PLAN:
            default:
                view = TeamUpgradePlanDialog;
                break;
        }
    }

    trackActivity("Upgrade", "ShowDialog", null, null, analytics);

    ShowDialog(view, {
        workspaceId,
        analytics,
    });
};
